import { useLayoutEffect, useRef, useState } from 'react';
import { CampaignAsset } from '../../lib/types';
import { parseDate, dayDiff, today } from '../../lib/dates';
import { assetDisplayTitle } from '../../lib/assetTitle';
import { ASSET_TYPE_ICON } from '../../lib/assetIcons';
import { Icon } from '../ui/Icon';

interface AssetGanttProps {
  assets: CampaignAsset[];
  onAssetClick: (a: CampaignAsset) => void;
  /** Row label. Defaults to the composed display title without campaign;
   *  planners that mix campaigns pass their own (with campaign prefix). */
  titleFor?: (a: CampaignAsset) => string;
  /** Bar color for the asset row. Falls back to a neutral grey. */
  colorFor?: (a: CampaignAsset) => string;
  /** Soft background tint for the bar. Falls back to a 20% alpha of the
   *  border color. */
  bgFor?: (a: CampaignAsset) => string;
  rangeWeeks?: number;
  startOffsetWeeks?: number;
  /** Label shown in the top-left corner header cell. */
  labelHeader?: string;
  /** Stretch the day columns so the timeline fills the available width
   *  (campaign pages, where the range is only a few weeks). */
  fitWidth?: boolean;
}

export const AssetGanttTimeline = ({
  assets,
  onAssetClick,
  titleFor = (a) => assetDisplayTitle(a),
  colorFor,
  bgFor,
  rangeWeeks = 12,
  startOffsetWeeks = -2,
  labelHeader = 'Placement',
  fitWidth = false,
}: AssetGanttProps) => {
  const TODAY = today();
  const boxRef = useRef<HTMLDivElement>(null);
  const [boxW, setBoxW] = useState(0);

  useLayoutEffect(() => {
    const el = boxRef.current;
    if (!fitWidth || !el) return;
    const measure = () => setBoxW(el.clientWidth);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [fitWidth]);

  const start = new Date(TODAY);
  start.setDate(start.getDate() + startOffsetWeeks * 7);
  const dow = (start.getDay() + 6) % 7;
  start.setDate(start.getDate() - dow);
  const totalDays = rangeWeeks * 7;

  const months: { key: string; label: string; count: number }[] = [];
  for (let i = 0; i < totalDays; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    const key = `${d.getFullYear()}-${d.getMonth()}`;
    const last = months[months.length - 1];
    if (!last || last.key !== key) {
      months.push({ key, label: d.toLocaleDateString('en', { month: 'short' }), count: 1 });
    } else {
      last.count++;
    }
  }

  const weeks: { index: number; date: Date }[] = [];
  for (let i = 0; i < totalDays; i += 7) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    weeks.push({ index: i, date: d });
  }

  const rowH = 30;
  const labelW = 280;
  const colW = fitWidth && boxW > 0 ? Math.max(14, (boxW - labelW) / totalDays) : 14;
  const totalW = totalDays * colW;

  // Undated assets don't appear on the Gantt — they belong to the "To
  // plan" tray. Filter them out before sorting.
  const sorted = [...assets]
    .filter((a): a is CampaignAsset & { publish_date: string } => !!a.publish_date)
    .sort((a, b) => parseDate(a.publish_date).getTime() - parseDate(b.publish_date).getTime());
  const totalH = sorted.length * rowH;
  const todayOffset = dayDiff(start, TODAY) * colW;

  return (
    <div className="gantt" ref={boxRef}>
      <div className="gantt-scroll">
        <div className="gantt-inner" style={{ width: labelW + totalW }}>
          <div className="gantt-header" style={{ gridTemplateColumns: `${labelW}px ${totalW}px` }}>
            <div className="gantt-corner">{labelHeader}</div>
            <div style={{ position: 'relative' }}>
              <div className="gantt-months">
                {months.map((m) => (
                  <div
                    key={m.key}
                    className="gantt-month"
                    style={{ width: m.count * colW, borderRight: '1px solid var(--border-soft)' }}
                  >
                    {m.label}
                  </div>
                ))}
              </div>
              <div className="gantt-weeks">
                {weeks.map((w) => (
                  <div key={w.index} className="gantt-week" style={{ width: 7 * colW }}>
                    <span>{w.date.getDate()}</span>
                  </div>
                ))}
              </div>
              {todayOffset >= 0 && todayOffset <= totalW && (
                <div className="gantt-today-pill" style={{ left: todayOffset }}>Today</div>
              )}
            </div>
          </div>

          <div className="gantt-body" style={{ gridTemplateColumns: `${labelW}px ${totalW}px` }}>
            <div className="gantt-labels">
              {sorted.map((a) => (
                <div
                  key={a.id}
                  className="gantt-label"
                  style={{ height: rowH, paddingLeft: 14 }}
                  onClick={() => onAssetClick(a)}
                  title={`${titleFor(a)} · ${a.asset_type}`}
                >
                  {/* The display name already restates the type, so the label
                      column shows the type as an icon instead of text. */}
                  <span style={{ color: 'var(--ink-4)', flexShrink: 0, display: 'inline-flex', marginRight: 7 }}>
                    <Icon name={ASSET_TYPE_ICON[a.asset_type] ?? 'image'} size={13} />
                  </span>
                  <span
                    className="gantt-label-name"
                    style={{ fontSize: 12, color: 'var(--ink)', minWidth: 0 }}
                  >
                    {titleFor(a)}
                  </span>
                </div>
              ))}
            </div>
            <div className="gantt-grid" style={{ height: totalH, width: totalW }}>
              {weeks.map((w) => (
                <div
                  key={w.index}
                  className="gantt-gridcol"
                  style={{ left: w.index * colW, width: 7 * colW }}
                />
              ))}
              {todayOffset >= 0 && todayOffset <= totalW && (
                <div className="gantt-today" style={{ left: todayOffset }} />
              )}
              {sorted.map((a, i) => {
                const s = parseDate(a.publish_date);
                const e = a.end_date ? parseDate(a.end_date) : s;
                const rawStartX = dayDiff(start, s) * colW;
                const rawEndX = (dayDiff(start, e) + 1) * colW;
                const x = Math.max(0, rawStartX);
                const endX = Math.min(totalW, rawEndX);
                const w = Math.max(0, endX - x);
                if (w === 0) return null;
                const isPast = e < TODAY;
                const isDone = a.status === 'Done';
                const color = colorFor ? colorFor(a) : 'var(--ink-3)';
                const bg = bgFor ? bgFor(a) : 'var(--bg-subtle)';
                const barH = 20;
                return (
                  <div
                    key={a.id}
                    className="gantt-bar"
                    onClick={() => onAssetClick(a)}
                    style={{
                      top: i * rowH + (rowH - barH) / 2,
                      left: x,
                      width: Math.max(24, w),
                      height: barH,
                      background: isPast || isDone ? 'transparent' : bg,
                      borderColor: color,
                      color,
                      opacity: isPast || isDone ? 0.55 : 1,
                      fontSize: 11.5,
                    }}
                    title={`${titleFor(a)} · ${a.asset_type} · ${a.status}`}
                  >
                    <span className="gantt-bar-dot" style={{ background: color }} />
                    <span className="gantt-bar-name">{titleFor(a)}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssetGanttTimeline;
